import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button as MuiButton,
  Paper,
  Dialog as MuiDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
} from '@mui/material';
import { toast } from '../../../../ui/toast';
import { DataTable, TableColumn } from '../../../../ui';
import { Applicant, TabData } from '../../../../utils/types/JobOpenings';
import {
  CCA_SUBDOMAINS,
  DUMMY_CCA_RESULTS,
} from '../../../questions/CCABank/ccaDummyData';
import { Column, GetColumns } from './Functions/GetColumns';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../utils/redux_store/store';
import { JobOpeningsContext } from '../../../../utils/contexts/JobOpening';

const DUMMY_CCA_RESULTS_ARRAY = Object.values(DUMMY_CCA_RESULTS);

type TableViewProps<T> = {
  candidates: Applicant[];
  loading?: boolean;
  tabTitles: string[];
  setTabTitles: (titles: string[]) => void | undefined;
  selectedCandidates: TabData[];
  setSelectedCandidates: (tabDataObject: TabData[]) => void | undefined;
  columns: Column[];
  setColumns: (value: ((prevState: Column[]) => Column[]) | Column[]) => void;
  getColumns: TableColumn<T>[];
  matchedSkills: string[];
  setActiveTab: (value: number) => void;
  aiScoreWeights?: { [key: string]: number };
  onMoveToUnsuccessful?: (candidates: Applicant[]) => void;
  onBulkStatusChange?: (
    candidates: Applicant[],
    targetStatus: string,
  ) => Promise<void> | void;
  onCandidateClick?: (
    candidate: Applicant,
    tileCandidates?: Applicant[],
    tileName?: string,
  ) => void;
  // Selection props (shared with 2D/3D chart)
  selectedApplicantsForEmail?: Applicant[];
  handleApplicantSelection?: (
    applicant: Applicant,
    isSelected?: boolean,
  ) => void;
  isApplicantSelected?: (applicant: Applicant) => boolean;
  selectAllUnsuccessful?: () => void;
  clearSelection?: () => void;
  handleSendEmailToSelected?: () => void;
  currentTab?: string;
};

type FormattedApplicant = {
  id: string;
  Applicant: string;
  Email: string;
  Avatar: string;
  'CV Score': string;
  Status: string;
  Location: string;
  City: string;
  Country: string;
  Experience: string;
  Age: string;
  Gender: string;
  Skills: string;
  [key: string]: string | number | boolean;
};

export const TableView = <T extends object>({
  candidates,
  loading = false,
  selectedCandidates,
  setSelectedCandidates,
  tabTitles,
  setTabTitles,
  getColumns,
  matchedSkills,
  setActiveTab,
  aiScoreWeights,
  onMoveToUnsuccessful,
  onBulkStatusChange,
  onCandidateClick,
  selectedApplicantsForEmail = [],
  handleApplicantSelection,
  isApplicantSelected,
  selectAllUnsuccessful,
  clearSelection,
  handleSendEmailToSelected,
  currentTab,
}: TableViewProps<FormattedApplicant>) => {
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [targetStatus, setTargetStatus] = useState<string>('');
  const [sortConfig, setSortConfig] = useState<
    { key: string; direction: 'asc' | 'desc' }[]
  >([{ key: 'CV Score', direction: 'desc' }]);
  const [showCCAColumns, setShowCCAColumns] = useState(false);
  const [retakenCandidates, setRetakenCandidates] = useState<Set<string>>(
    new Set(),
  );
  const [retakeConfirmId, setRetakeConfirmId] = useState<string | null>(null);
  const rowsPerPage = 25;

  const { subscriptions } = useSelector((state: RootState) => state.subscriptions);
  const { jobOpeningElements } = useContext(JobOpeningsContext);

  const onPageChange = (page: number) => {
    const maxPages = Math.max(1, Math.ceil(candidates.length / rowsPerPage));
    const nextPage = Math.min(Math.max(page, 1), maxPages);
    setPageNumber(nextPage);
  };

  const sortCandidates = (
    data: Applicant[],
    sortKey: string,
    direction: 'asc' | 'desc',
  ) => {
    return [...data].sort((a, b) => {
      let aValue: number | string = '';
      let bValue: number | string = '';

      if (sortKey === 'CV Score' || sortKey === 'aiScore') {
        aValue = a.aiScore ?? 0;
        bValue = b.aiScore ?? 0;
      } else if (sortKey === 'Experience') {
        aValue = a.experience ?? a.value?.[1] ?? 0;
        bValue = b.experience ?? b.value?.[1] ?? 0;
      } else if (sortKey === 'Age') {
        aValue = a.age ?? 0;
        bValue = b.age ?? 0;
      } else if (sortKey === 'Skills') {
        aValue = a.noOfSkills ?? a.skills?.length ?? 0;
        bValue = b.noOfSkills ?? b.skills?.length ?? 0;
      } else {
        aValue = (a as any)[sortKey] ?? '';
        bValue = (b as any)[sortKey] ?? '';
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return direction === 'desc' ? bValue - aValue : aValue - bValue;
      } else {
        return direction === 'desc'
          ? String(bValue).localeCompare(String(aValue))
          : String(aValue).localeCompare(String(bValue));
      }
    });
  };

  // onSortChange from DataTable
  const handleSortChange = (key: string, direction: 'asc' | 'desc') => {
    setSortConfig([{ key, direction }]);
  };

  const onClickTab = (row: FormattedApplicant) => {
    const candidate = candidates.find((item) => item.userId === row.id);

    // Use the new onCandidateClick prop if available, otherwise use the old tab system
    if (onCandidateClick && candidate) {
      onCandidateClick(candidate);
      return;
    }

    // Fallback to old tab system
    const nameExists = selectedCandidates.some(
      (item) => item.candidate && item.candidate.userId === row.id,
    );

    if (!nameExists) {
      const updatedTabTitles = [...tabTitles, row.Applicant];
      const selectedItem: Applicant | undefined = candidates.find(
        (item) => item.userId === row.id,
      );
      const newSelectedCandidates = [
        ...selectedCandidates,
        {
          tabId: tabTitles.length,
          candidate: selectedItem,
        },
      ];
      setSelectedCandidates(newSelectedCandidates);
      setTabTitles(updatedTabTitles);
      setActiveTab(tabTitles.length);
    } else {
      toast.error('Tab for ' + row.Applicant + ' already open', {
        position: 'bottom-center',
      });
    }
  };

  const formatApplicants = (data: Applicant[]): FormattedApplicant[] => {
    return data.map((applicant, idx) => {
      const yoe = applicant.experience ?? applicant.value?.[1] ?? 0;
      const city = applicant.locationCity || '';
      const country = applicant.locationCountry || '';
      const skillsCount = applicant.noOfSkills ?? applicant.skills?.length ?? 0;

      const formatted: FormattedApplicant = {
        id: applicant.userId,
        Applicant: `${applicant.firstName ?? ''} ${
          applicant.lastName ?? ''
        }`.trim(),
        Email: applicant.email || '',
        Avatar: applicant.base64Encoded || '',
        'CV Score': `${applicant.aiScore}%`,
        Status: applicant.applicantStatus,
        Location: [city, country].filter(Boolean).join(', '),
        City: city,
        Country: country,
        Experience: yoe ? `${yoe} yr${yoe === 1 ? '' : 's'}` : '—',
        Age: applicant.age != null ? String(applicant.age) : '—',
        Gender: applicant.gender || '—',
        Skills: skillsCount ? String(skillsCount) : '—',
      };

      matchedSkills.forEach((skill) => {
        const hasSkill = applicant.skills.some(
          (matched) => matched.toLowerCase() === skill.toLowerCase(),
        );

        const upperCased = skill.toUpperCase();
        formatted[upperCased] = hasSkill ? 'Yes' : 'No';
      });

      // Add CCA data if available
      const dummyIdx = idx % DUMMY_CCA_RESULTS_ARRAY.length;
      const ccaResult = retakenCandidates.has(applicant.userId)
        ? {
          status: 'invite-sent' as const,
          totalScore: null,
          subdomainScores: {},
        }
        : DUMMY_CCA_RESULTS_ARRAY[dummyIdx];

      formatted['CCA Status'] = retakenCandidates.has(applicant.userId)
        ? 'invite-sent'
        : ccaResult.status;
      formatted['CCA Total'] =
        ccaResult.totalScore != null ? `${ccaResult.totalScore}%` : '—';
      CCA_SUBDOMAINS.forEach((sd) => {
        const score = (ccaResult as any).subdomainScores?.[sd];
        formatted[`CCA ${sd}`] = score != null ? `${score}%` : '—';
      });

      return formatted;
    });
  };

  const sortedCandidates =
    sortConfig.length > 0
      ? sortCandidates(candidates, sortConfig[0].key, sortConfig[0].direction)
      : candidates;

  const changedData = formatApplicants(sortedCandidates);
  const totalPages = Math.max(1, Math.ceil(changedData.length / rowsPerPage));

  useEffect(() => {
    if (pageNumber > totalPages) {
      setPageNumber(totalPages);
    }
  }, [pageNumber, totalPages]);

  const paginatedData = changedData.slice(
    (pageNumber - 1) * rowsPerPage,
    pageNumber * rowsPerPage,
  );

  const moveStatusOptions = [
    'New',
    'Suitable',
    'Outstanding',
    'Shortlisted',
    'Auto-Assessment',
    'Unsuccessful-Pending',
    ...(jobOpeningElements?.includes('Wellness') ? ['Wellness'] : []),
    ...(jobOpeningElements?.includes('Psychometric')
        ? ['Psychometric']
        : []),
  ];

  const isUnsuccessfulTab = currentTab === 'Unsuccessful Applicants';
  const selectedCount = selectedApplicantsForEmail.length;

  const ccaStatusColors: Record<string, { bg: string; color: string }> = {
    'invite-sent': { bg: '#f1f5f9', color: '#475569' },
    'in-progress': { bg: '#fef3c7', color: '#b45309' },
    completed: { bg: '#d1fae5', color: '#047857' },
  };

  const ccaColumns: TableColumn<FormattedApplicant>[] = showCCAColumns
    ? [
      {
        key: 'CCA Status',
        label: 'CCA Status',
        disableSort: true,
        render: (row) => {
          const status = String(row['CCA Status'] || '');
          const colors = ccaStatusColors[status] ?? { bg: '#f1f5f9', color: '#64748b' };
          return (
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.25,
                borderRadius: '9999px',
                fontSize: '11px',
                fontWeight: 600,
                bgcolor: colors.bg,
                color: colors.color,
              }}
            >
              {status.replace('-', ' ')}
            </Box>
          );
        },
      },
      {
        key: 'CCA Total',
        label: 'CCA Total',
        align: 'center' as const,
      },
      ...CCA_SUBDOMAINS.map((sd) => ({
        key: `CCA ${sd}`,
        label: sd
          .split(' ')
          .map((w) => w[0])
          .join(''),
        align: 'center' as const,
        disableSort: true,
      })),
      {
        key: 'cca-retake',
        label: 'Retake',
        disableSort: true,
        align: 'center' as const,
        render: (row: FormattedApplicant) => (
          <Box
            component="button"
            type="button"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              setRetakeConfirmId(row.id);
            }}
            sx={{
              px: 1,
              py: 0.5,
              borderRadius: '8px',
              fontSize: '11px',
              fontWeight: 600,
              bgcolor: '#f5f3ff',
              color: '#7c3aed',
              border: '1px solid #ddd6fe',
              cursor: 'pointer',
              transition: 'background-color 0.15s ease',
              '&:hover': {
                bgcolor: '#ede9fe',
              },
            }}
          >
            Retake
          </Box>
        ),
      },
    ]
    : [];

  const allColumns = [...getColumns, ...ccaColumns];

  // Determine if all visible table applicants are selected
  const allSelected =
    candidates.length > 0 && candidates.every((c) => isApplicantSelected?.(c));

  const handleSelectAll = () => {
    if (allSelected) {
      clearSelection?.();
    } else {
      candidates.forEach((candidate) => {
        handleApplicantSelection?.(candidate, true);
      });
    }
  };

  const handleRowSelect = (_rowIndex: number, row: FormattedApplicant) => {
    const candidate = candidates.find((c) => c.userId === row.id);
    if (candidate) {
      handleApplicantSelection?.(candidate);
    }
  };

  const isRowSelected = (_rowIndex: number, row: FormattedApplicant) => {
    const candidate = candidates.find((c) => c.userId === row.id);
    return candidate ? isApplicantSelected?.(candidate) ?? false : false;
  };

  const handleRowClick = (row: FormattedApplicant) => {
    // Keep row click and checkbox selection as separate actions.
    // Row click opens candidate profile/tab, checkbox controls selection.
    onClickTab(row);
  };

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%', overflowX: 'auto', p: 2 }}>
      {/* Retake CCA Confirmation Modal */}
      <MuiDialog
        open={!!retakeConfirmId}
        onClose={() => setRetakeConfirmId(null)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Retake CCA?</DialogTitle>
        <DialogContent>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            This will reset the candidate&apos;s CCA status to &quot;Invite
            Sent&quot; and clear their current scores.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <MuiButton onClick={() => setRetakeConfirmId(null)} sx={{ textTransform: 'none' }}>
            Cancel
          </MuiButton>
          <MuiButton
            variant='contained'
            color='secondary'
            onClick={() => {
              setRetakenCandidates(
                (prev) =>
                  new Set(Array.from(prev).concat(retakeConfirmId!)),
              );
              setRetakeConfirmId(null);
            }}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Confirm Retake
          </MuiButton>
        </DialogActions>
      </MuiDialog>

      {/* Show CCA toggle */}
      <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
        <MuiButton
          variant={showCCAColumns ? 'contained' : 'outlined'}
          color='secondary'
          size='small'
          onClick={() => setShowCCAColumns((v) => !v)}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
        >
          {showCCAColumns ? 'Hide CCA' : 'Show CCA'}
        </MuiButton>
      </Box>

      {aiScoreWeights !== null && (
        <Paper
          variant='outlined'
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 2,
            bgcolor: 'action.hover',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant='subtitle2' sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            CV Score Weights
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {Object.entries(aiScoreWeights ?? {})
              .filter(([key]) => key.toLowerCase() !== 'keyword')
              .map(([key, value]) => (
                <Paper
                  key={key}
                  elevation={0}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    minWidth: 70,
                  }}
                >
                  <Typography variant='caption' sx={{ fontSize: '0.625rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>
                    {key
                      .replace(/([A-Z])/g, ' $1')
                      .replace(/^./, (str) => str.toUpperCase())}
                  </Typography>
                  <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
                    {value}%
                  </Typography>
                </Paper>
              ))}
          </Box>
        </Paper>
      )}

      {/* Selection Action Bar */}
      {handleApplicantSelection && (
        <Paper
          variant='outlined'
          sx={{
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1,
            borderRadius: 2,
            bgcolor: selectedCount > 0 ? 'action.selected' : 'action.hover',
            borderColor: selectedCount > 0 ? 'primary.main' : 'divider',
            flexWrap: 'wrap',
          }}
        >
          {/* Select All Unsuccessful */}
          {isUnsuccessfulTab && (
            <MuiButton
              variant='outlined'
              size='small'
              onClick={handleSelectAll}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              {allSelected ? 'Deselect All' : 'Select All Pending'}
            </MuiButton>
          )}

          {/* Selected count */}
          <Typography variant='body2' sx={{ fontWeight: 600, color: selectedCount > 0 ? 'primary.main' : 'text.secondary' }}>
            {selectedCount > 0
              ? `${selectedCount} selected`
              : 'No selection — use checkboxes to select applicants'}
          </Typography>

          <Box sx={{ flex: 1 }} />

          {/* Move to Unsuccessful / Bulk Status Change */}
          {onBulkStatusChange && selectedCount > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Select
                size='small'
                value={targetStatus}
                onChange={(e) => setTargetStatus(e.target.value)}
                displayEmpty
                sx={{ fontSize: '0.75rem', height: 32, bgcolor: 'background.paper' }}
              >
                <MenuItem value=''>Move to status...</MenuItem>
                {moveStatusOptions.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
              <MuiButton
                variant='contained'
                color='warning'
                size='small'
                onClick={() => {
                  if (!targetStatus) return;
                  onBulkStatusChange(selectedApplicantsForEmail, targetStatus);
                  setTargetStatus('');
                }}
                disabled={!targetStatus}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Apply
              </MuiButton>
            </Box>
          )}

          {!onBulkStatusChange && onMoveToUnsuccessful && selectedCount > 0 && (
            <MuiButton
              variant='contained'
              color='warning'
              size='small'
              onClick={() => onMoveToUnsuccessful(selectedApplicantsForEmail)}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              Move to Unsuccessful
            </MuiButton>
          )}

          {/* Send Email */}
          {handleSendEmailToSelected && selectedCount > 0 && (
            <MuiButton
              variant='contained'
              color='error'
              size='small'
              onClick={handleSendEmailToSelected}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              Email ({selectedCount})
            </MuiButton>
          )}

          {/* Clear selection */}
          {selectedCount > 0 && (
            <MuiButton
              variant='outlined'
              size='small'
              onClick={clearSelection}
              sx={{ textTransform: 'none' }}
            >
              Clear
            </MuiButton>
          )}
        </Paper>
      )}

      <DataTable
        isLoading={loading}
        columns={allColumns as TableColumn<FormattedApplicant>[]}
        data={paginatedData}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        currentPage={pageNumber}
        onClickRow={handleRowClick}
        totalPages={totalPages}
        totalRecords={changedData.length}
        sortConfig={sortConfig}
        onSortChange={handleSortChange}
        hasCheckBox={!!handleApplicantSelection}
        onSelectAll={handleSelectAll}
        onSelectRow={handleRowSelect}
        isRowSelected={isRowSelected}
        isAllSelected={allSelected}
      />
    </Box>
  );
};
